import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Trash2, Shield, Lock, FileText } from 'lucide-react';

const DataDeletion = () => {
  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-amber-600 hover:text-amber-700 transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver a la página principal
          </Link>
        </div>
        
        <div className="bg-white rounded-xl shadow-md p-6 sm:p-8 md:p-10">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Instrucciones para la eliminación de datos</h1>
          <p className="text-gray-600 mb-4">Última actualización: {new Date().toLocaleDateString()}</p>
          
          <div className="prose prose-amber max-w-none">
            <p className="lead text-lg text-gray-700">
              En DogScan AI nos comprometemos a proteger su privacidad y a garantizar que tenga el control sobre 
              sus datos personales. Esta página explica cómo puede solicitar la eliminación de sus datos en 
              nuestra plataforma.
            </p>
            
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-6 my-6">
              <h2 className="flex items-center text-amber-800 mb-4">
                <Trash2 className="mr-2 h-5 w-5" />
                Solicitud de eliminación de datos
              </h2>
              <p className="text-amber-700 mb-4">
                Respetamos su derecho a solicitar la eliminación de sus datos personales. Puede enviarnos una
                solicitud de eliminación de datos por correo electrónico, y procesaremos su solicitud de acuerdo
                con nuestras obligaciones legales.
              </p>
              <div className="flex items-center bg-white p-4 rounded-lg border border-amber-200">
                <Mail className="text-amber-600 mr-3 h-5 w-5" />
                <p className="font-medium">Envíe su solicitud a: <a href="mailto:contacto@dogscanai.com" className="text-amber-600 hover:text-amber-700">contacto@dogscanai.com</a></p>
              </div>
            </div>
            
            <h2 className="flex items-center mt-8">
              <Shield className="mr-2 h-5 w-5 text-amber-600" />
              Proceso de eliminación de datos
            </h2>
            
            <p>
              Para proteger su privacidad, seguimos un proceso estricto para verificar todas las solicitudes de eliminación
              de datos. A continuación se detalla el proceso completo:
            </p>
            
            <ol className="space-y-4 mt-4">
              <li className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                <strong className="text-amber-700 block mb-1">1. Envíe su solicitud</strong>
                <p>
                  Envíe un correo electrónico a <a href="mailto:conacto@dogscanai.com" className="text-amber-600">contacto@dogscanai.com</a> con 
                  el asunto "Solicitud de eliminación de datos". Incluya su nombre completo y la dirección de correo electrónico 
                  asociada con su cuenta en DogScan AI.
                </p>
              </li>
              
              <li className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                <strong className="text-amber-700 block mb-1">2. Verificación de identidad</strong>
                <p>
                  Para proteger sus datos, necesitamos verificar su identidad. Le enviaremos un correo electrónico 
                  de verificación a la dirección asociada con su cuenta. Este paso garantiza que solo el 
                  titular legítimo de la cuenta pueda solicitar la eliminación de datos.
                </p>
              </li>
              
              <li className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                <strong className="text-amber-700 block mb-1">3. Confirmación y procesamiento</strong>
                <p>
                  Una vez verificada su identidad, confirmaremos la recepción de su solicitud y comenzaremos 
                  el proceso de eliminación. El proceso puede tardar hasta 30 días en completarse.
                </p>
              </li>
              
              <li className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                <strong className="text-amber-700 block mb-1">4. Eliminación de datos</strong>
                <p>
                  Eliminaremos todos sus datos personales de nuestros sistemas, incluidos, pero no limitados a:
                </p>
                <ul className="mt-2 space-y-1 text-gray-700">
                  <li>• Información de su cuenta y perfil</li>
                  <li>• Imágenes y datos de mascotas subidos</li>
                  <li>• Historial de análisis e informes</li>
                  <li>• Registros de actividad y preferencias</li>
                </ul>
              </li>
              
              <li className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                <strong className="text-amber-700 block mb-1">5. Confirmación final</strong>
                <p>
                  Una vez que hayamos completado el proceso de eliminación, le enviaremos un correo 
                  electrónico final confirmando que sus datos han sido eliminados de nuestros sistemas.
                </p>
              </li>
            </ol>
            
            <h2 className="flex items-center mt-8">
              <Lock className="mr-2 h-5 w-5 text-amber-600" />
              Retención de datos y excepciones
            </h2>
            
            <p>
              En determinadas circunstancias, es posible que estemos obligados a conservar ciertos datos para 
              cumplir con obligaciones legales, resolver disputas o hacer cumplir nuestros acuerdos. En estos casos:
            </p>
            
            <ul className="space-y-2 mt-4">
              <li>
                Solo conservaremos la información mínima necesaria para estos fines.
              </li>
              <li>
                Los datos retenidos solo se utilizarán para el propósito legal específico por el que se retienen.
              </li>
              <li>
                Una vez que no exista la necesidad legal de retener los datos, procederemos a eliminarlos de forma segura.
              </li>
            </ul>
            
            <h2 className="flex items-center mt-8">
              <FileText className="mr-2 h-5 w-5 text-amber-600" />
              Datos compartidos con terceros
            </h2>
            
            <p>
              Si ha utilizado servicios de inicio de sesión de terceros (como Facebook, Google o Apple) para 
              acceder a DogScan AI, tenga en cuenta que:
            </p>
            
            <ul className="space-y-2 mt-4">
              <li>
                Eliminaremos todos los datos almacenados en nuestros sistemas.
              </li>
              <li>
                Para los datos que compartió directamente con estos proveedores de servicios de terceros, deberá 
                contactar directamente con ellos para solicitar la eliminación de esos datos.
              </li>
              <li>
                Proporcionamos enlaces a las políticas de privacidad de estos proveedores en nuestra <Link to="/privacy-policy" className="text-amber-600 hover:text-amber-700">Política de Privacidad</Link>.
              </li>
            </ul>
            
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 my-8">
              <h3 className="text-gray-800 mb-3">Información adicional</h3>
              <p className="text-gray-700 mb-2">
                Si tiene alguna pregunta adicional sobre el proceso de eliminación de datos o necesita más 
                información, no dude en contactarnos:
              </p>
              <p className="font-medium text-gray-800">
                Correo electrónico: <a href="mailto:contacto@dogscanai.com" className="text-amber-600 hover:text-amber-700">contacto@dogscanai.com</a>
              </p>
              <p className="mt-4 text-sm text-gray-600">
                Consulte también nuestra <Link to="/privacy-policy" className="text-amber-600 hover:text-amber-700">Política de Privacidad</Link> y 
                los <Link to="/terms-of-service" className="text-amber-600 hover:text-amber-700">Términos de Servicio</Link> para 
                obtener más información sobre cómo tratamos sus datos.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataDeletion; 